import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader, Row, Col } from 'reactstrap';
import api from '../../../api';
import DataTable from './DataTable';
import MetricCard from './MetricCard';
import RefreshTimer from './RefreshTimer';

const employeeCols = [
    { key: 'EmployeeName', label: 'Employee Name' },
    { key: 'Direct', label: 'Direct' },
    { key: 'Indirect', label: 'Indirect' },
    { key: 'Description', label: 'Description' },
    { key: 'JobNum', label: 'Job Num', cellStyle: { fontWeight: 700, fontFamily: '"Courier New", monospace', fontSize: 14 } },
    { key: 'PartNum', label: 'Part Num' },
    { key: 'JobQty', label: 'Job Qty' },
    { key: 'StartTime', label: 'Start Time' },
];

const QualityTab = () => {
    const [employees, setEmployees] = useState([]);
    const [totals, setTotals] = useState(null);
    const [dmr, setDmr] = useState(null);
    const [rma, setRma] = useState(null);
    const [hundredParts, setHundredParts] = useState(null);
    const [qfinal, setQfinal] = useState(null);
    const [incoming, setIncoming] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await api.get('/display-board/quality');
            const d = res.data;
            setEmployees(d?.employees || []);
            setTotals(d?.totals || null);
            setDmr(d?.dmr || null);
            setRma(d?.rma || null);
            setHundredParts(d?.hundredParts || null);
            setQfinal(d?.qfinal || null);
            setIncoming(d?.incoming || null);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load Quality data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Helper to get val from totals (handles case-insensitive JSON keys)
    const tv = (key) => {
        if (!totals) return '—';
        return totals[key] ?? totals[key.charAt(0).toLowerCase() + key.slice(1)] ?? '—';
    };

    return (
        <>
            {/* Totals Row */}
            <Row className="g-3 mb-3">
                <Col md={4} lg={2}>
                    <MetricCard title="QFinal" color="primary" icon="ri-check-double-line" loading={loading}
                        metrics={totals ? [{ label: 'Today', value: tv('QFinal') }] : null} />
                </Col>
                <Col md={4} lg={2}>
                    <MetricCard title="QInspect" color="info" icon="ri-search-eye-line" loading={loading}
                        metrics={totals ? [{ label: 'Today', value: tv('QInspect') }] : null} />
                </Col>
                <Col md={4} lg={2}>
                    <MetricCard title="QInProc" color="success" icon="ri-tools-line" loading={loading}
                        metrics={totals ? [{ label: 'Today', value: tv('QInProc') }] : null} />
                </Col>
                <Col md={4} lg={2}>
                    <MetricCard title="First Piece" color="warning" icon="ri-medal-line" loading={loading}
                        metrics={totals ? [{ label: 'Today', value: tv('FirstPiece') }] : null} />
                </Col>
                <Col md={4} lg={2}>
                    <MetricCard title="QFair" color="secondary" icon="ri-scales-3-line" loading={loading}
                        metrics={totals ? [{ label: 'Today', value: tv('QFair') }] : null} />
                </Col>
                <Col md={4} lg={2}>
                    <MetricCard title="QDoc" color="dark" icon="ri-file-text-line" loading={loading}
                        metrics={totals ? [{ label: 'Today', value: tv('QDoc') }] : null} />
                </Col>
            </Row>

            {/* Employee Grid */}
            <Card className="border-0 shadow-sm mb-3">
                <CardHeader className="border-bottom d-flex justify-content-between align-items-center py-3">
                    <h6 className="mb-0 fw-semibold">
                        <i className="ri-shield-check-line me-2 text-primary"></i>
                        Quality Department — Display Board
                    </h6>
                    <RefreshTimer onRefresh={fetchData} interval={60} />
                </CardHeader>
                <CardBody className="p-0">
                    {error && (
                        <div className="alert alert-danger m-3 mb-0" role="alert">
                            <i className="ri-error-warning-line me-2"></i>{error}
                        </div>
                    )}
                    <DataTable
                        highlightKey="JobNum"  // Round DB-1 — green = active job
                        columns={employeeCols}
                        rows={employees}
                        loading={loading}
                        emptyMessage="No Quality employees clocked in today"
                    />
                </CardBody>
            </Card>

            {/* Bottom Metrics */}
            <Row className="g-3">
                <Col md={6} lg={3}>
                    <MetricCard
                        title="RMA"
                        color="danger"
                        icon="ri-exchange-line"
                        loading={loading}
                        metrics={rma ? [
                            { label: 'Open', value: rma.OpenRMA ?? rma.openRMA },
                            { label: 'Received', value: rma.Received ?? rma.received },
                            { label: 'Not Received', value: rma.NotReceived ?? rma.notReceived },
                            { label: 'Avg Wait Days', value: rma.AvgWaitDays ?? rma.avgWaitDays },
                        ] : null}
                    />
                </Col>
                <Col md={6} lg={3}>
                    <MetricCard
                        title="DMR"
                        color="warning"
                        icon="ri-file-warning-line"
                        loading={loading}
                        metrics={dmr ? [
                            { label: 'Count', value: dmr.DmrCount ?? dmr.dmrCount },
                            { label: 'Avg Wait Days', value: dmr.AvgWaitDays ?? dmr.avgWaitDays },
                        ] : null}
                    />
                </Col>
                <Col md={6} lg={3}>
                    <MetricCard
                        title="QFinal Queue"
                        color="primary"
                        icon="ri-check-double-line"
                        loading={loading}
                        metrics={qfinal ? [
                            { label: 'Count', value: qfinal.QfinalCount ?? qfinal.qfinalCount },
                            { label: 'Avg Wait Days', value: qfinal.AvgWaitDays ?? qfinal.avgWaitDays },
                        ] : null}
                    />
                </Col>
                <Col md={6} lg={3}>
                    <MetricCard
                        title="Incoming Inspection"
                        color="info"
                        icon="ri-inbox-archive-line"
                        loading={loading}
                        metrics={incoming ? [
                            { label: 'Count', value: incoming.IncomingCount ?? incoming.incomingCount },
                            { label: 'Avg Wait Days', value: incoming.AvgWaitDays ?? incoming.avgWaitDays },
                        ] : null}
                    />
                </Col>
            </Row>

            {hundredParts && (
                <Row className="g-3 mt-1">
                    <Col md={4}>
                        <MetricCard
                            title="100 Parts (Days)"
                            color="secondary"
                            icon="ri-timer-line"
                            loading={loading}
                            metrics={[{ label: 'Avg', value: hundredParts.HundredPartsDays ?? hundredParts.hundredPartsDays }]}
                        />
                    </Col>
                </Row>
            )}
        </>
    );
};

export default QualityTab;